import React, { useCallback, useEffect, useState } from "react";
import { Button, Descriptions, Form, Input, List, Modal, Radio, Select, Spin, Steps, message } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { onboardDealer, searchDealerships } from "../../actions/Actions";
import logo from "../../images/FP_Logo.png";
import "./DealershipSignup.css";
import LoadingOverlay from "../LoadingOverlay/LoadingOverlay";

const US_STATES = [
  { value: "AL", label: "Alabama" },
  { value: "AK", label: "Alaska" },
  { value: "AZ", label: "Arizona" },
  { value: "AR", label: "Arkansas" },
  { value: "CA", label: "California" },
  { value: "CO", label: "Colorado" },
  { value: "CT", label: "Connecticut" },
  { value: "DE", label: "Delaware" },
  { value: "DC", label: "District of Columbia" },
  { value: "FL", label: "Florida" },
  { value: "GA", label: "Georgia" },
  { value: "HI", label: "Hawaii" },
  { value: "ID", label: "Idaho" },
  { value: "IL", label: "Illinois" },
  { value: "IN", label: "Indiana" },
  { value: "IA", label: "Iowa" },
  { value: "KS", label: "Kansas" },
  { value: "KY", label: "Kentucky" },
  { value: "LA", label: "Louisiana" },
  { value: "ME", label: "Maine" },
  { value: "MD", label: "Maryland" },
  { value: "MA", label: "Massachusetts" },
  { value: "MI", label: "Michigan" },
  { value: "MN", label: "Minnesota" },
  { value: "MS", label: "Mississippi" },
  { value: "MO", label: "Missouri" },
  { value: "MT", label: "Montana" },
  { value: "NE", label: "Nebraska" },
  { value: "NV", label: "Nevada" },
  { value: "NH", label: "New Hampshire" },
  { value: "NJ", label: "New Jersey" },
  { value: "NM", label: "New Mexico" },
  { value: "NY", label: "New York" },
  { value: "NC", label: "North Carolina" },
  { value: "ND", label: "North Dakota" },
  { value: "OH", label: "Ohio" },
  { value: "OK", label: "Oklahoma" },
  { value: "OR", label: "Oregon" },
  { value: "PA", label: "Pennsylvania" },
  { value: "RI", label: "Rhode Island" },
  { value: "SC", label: "South Carolina" },
  { value: "SD", label: "South Dakota" },
  { value: "TN", label: "Tennessee" },
  { value: "TX", label: "Texas" },
  { value: "UT", label: "Utah" },
  { value: "VT", label: "Vermont" },
  { value: "VA", label: "Virginia" },
  { value: "WA", label: "Washington" },
  { value: "WV", label: "West Virginia" },
  { value: "WI", label: "Wisconsin" },
  { value: "WY", label: "Wyoming" }
];

const ZIP_PATTERN = /^\d{5}$/;
const PHONE_PATTERN = /^\d{10}$/;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CONTACT_FIELDS = ["contact_name", "contact_position", "contact_mobile", "contact_email"];

const DealershipSignup = () => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.loading);
  const [searchText, setSearchText] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedDealershipKeys, setSelectedDealershipKeys] = useState([]);
  const [selectedDealerships, setSelectedDealerships] = useState([]);
  const [currentStep, setCurrentStep] = useState(0);
  const [locationForm] = Form.useForm();
  const [contactForm] = Form.useForm();
  const [locationFilters, setLocationFilters] = useState(null);
  const [canProceedStep2, setCanProceedStep2] = useState(false);
  const [rulesAcknowledgement, setRulesAcknowledgement] = useState(null);

  const handleLocationZipChange = (event) => {
    const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, 5);
    locationForm.setFieldsValue({ dealer_zip: digitsOnly });
  };

  const handleMobileChange = (event) => {
    const digitsOnly = event.target.value.replace(/\D/g, "");
    contactForm.setFieldsValue({ contact_mobile: digitsOnly });
  };

  const updateStep2Ready = useCallback(() => {
    const values = contactForm.getFieldsValue(CONTACT_FIELDS);
    const allFilled = CONTACT_FIELDS.every((field) => {
      const value = values[field];
      return value !== undefined && value !== null && String(value).trim() !== "";
    });
    const errors = contactForm.getFieldsError(CONTACT_FIELDS);
    const hasErrors = errors.some((field) => field.errors.length > 0);
    setCanProceedStep2(allFilled && !hasErrors);
  }, [contactForm]);

  const resetSelectedDealerships = useCallback(() => {
    setSelectedDealerships([]);
    setSelectedDealershipKeys([]);
  }, []);

  const getDealershipKey = useCallback(
    (item) => String(item?.fp_dealer_id || [
      item?.seller_name,
      item?.dealership_group_name,
      item?.street,
      item?.city,
      item?.state,
      item?.zip
    ].filter(Boolean).join("|")),
    []
  );

  const formatDealershipAddress = (item) => {
    const line1 = item?.street || "";
    const line2 = [item?.city, item?.state, item?.zip].filter(Boolean).join(", ");
    if (line1 && line2) return `${line1}, ${line2}`;
    return line1 || line2;
  };

  const getDealershipWebsite = (item) => item?.dealer_website || "";
  const getDealershipEmail = (item) => item?.email || "";
  const getDealershipPhone = (item) => item?.phone || "";

  const formatDealershipPhone = (item) => {
    const raw = getDealershipPhone(item);
    if (Array.isArray(raw)) {
      return raw.filter(Boolean).join(", ");
    }
    if (raw == null) return "";
    return String(raw);
  };

  const toggleDealershipSelection = useCallback((item) => {
    const itemKey = getDealershipKey(item);
    setSelectedDealershipKeys((currentKeys) =>
      currentKeys.includes(itemKey)
        ? currentKeys.filter((key) => key !== itemKey)
        : [...currentKeys, itemKey]
    );
    setSelectedDealerships((currentDealerships) => {
      const exists = currentDealerships.some((dealership) => getDealershipKey(dealership) === itemKey);
      if (exists) {
        return currentDealerships.filter((dealership) => getDealershipKey(dealership) !== itemKey);
      }
      return [...currentDealerships, item];
    });
  }, [getDealershipKey]);

  const handleLocationNext = async () => {
    try {
      const values = await locationForm.validateFields();
      const nextFilters = {
        dealer_state: String(values.dealer_state || "").trim().toUpperCase(),
        dealer_city: String(values.dealer_city || "").trim(),
        dealer_zip: String(values.dealer_zip || "").trim()
      };
      setLocationFilters(nextFilters);
      setSearchText("");
      setSearchResults([]);
      resetSelectedDealerships();
      setCurrentStep(1);
    } catch (err) {
      return;
    }
  };

  const handleFinish = async () => {
    const contactData = contactForm.getFieldsValue(CONTACT_FIELDS);
    const payload = {
      dealerships: selectedDealerships,
      contact: contactData,
      acknowledgement: rulesAcknowledgement,
      signup_location: locationFilters
    };
    dispatch({ type: "SET_LOADING" });
    try {
      await onboardDealer(payload);
      Modal.success({
        title: "Signup Complete",
        content: "Dealership onboarding completed successfully.",
        okText: "Login",
        onOk: () => {
          window.location.href = "/";
        }
      });
    } catch (err) {
      console.error("Onboard error:", err);
      const apiMessage = err?.response?.data?.message || err?.response?.data?.error;
      message.error(apiMessage || "Unable to complete signup. Please try again.");
    } finally {
      dispatch({ type: "RESET_LOADING" });
    }
  };

  useEffect(() => {
    dispatch({ type: "RESET_LOADING" });
    dispatch({ type: "RESET_LOAN_DATA" });
    dispatch({ type: "RESET_LOAN_ACCESS_REQUEST_SENT" });
  }, [dispatch]);

  useEffect(() => {
    if (currentStep === 2) {
      updateStep2Ready();
    }
  }, [currentStep, updateStep2Ready]);

  useEffect(() => {
    if (currentStep !== 1 || !locationFilters) {
      return;
    }

    let isActive = true;
    const trimmed = searchText.trim();

    if (trimmed.length < 2) {
      setSearchResults([]);
      setIsSearching(false);
      return () => {};
    }

    setIsSearching(true);
    const timeoutId = setTimeout(async () => {
      try {
        const results = await searchDealerships(trimmed, {
          state: locationFilters.dealer_state,
          city: locationFilters.dealer_city,
          zip: locationFilters.dealer_zip
        });
        if (!isActive) return;
        setSearchResults(Array.isArray(results) ? results : []);
      } catch (err) {
        if (!isActive) return;
        setSearchResults([]);
      } finally {
        if (isActive) setIsSearching(false);
      }
    }, 300);

    return () => {
      isActive = false;
      clearTimeout(timeoutId);
    };
  }, [currentStep, locationFilters, searchText]);

  return (
    <>
      <div className="outer-container">
        {isLoading && <LoadingOverlay message="Signing Up" />}
        <div className="inner-container">
          <img
            alt="logo"
            style={{
              marginLeft: "auto",
              marginRight: "auto",
              display: "block"
            }}
            src={logo}
            height="120"
          />
          <h1 style={{ marginLeft: "35%" }}>Dealership Onboarding</h1>
          <div className="signup-panel">
            <Steps
              className="signup-steps"
              current={currentStep}
              items={[
                { title: "Location" },
                { title: "Dealership" },
                { title: "Contact" },
                { title: "Authorization" }
              ]}
            />

            {currentStep === 0 ? (
              <div className="signup-step signup-step-location">
                <div className="signup-panel-header">
                  <h2>Step 1: Dealer Location</h2>
                  <p>
                    Enter the dealership&apos;s location first. We will use these values to filter the dealer search.
                  </p>
                </div>

                <Form
                  form={locationForm}
                  layout="vertical"
                  autoComplete="off"
                  className="signup-location-form"
                >
                  <Form.Item
                    label="State"
                    name="dealer_state"
                    rules={[
                      {
                        required: true,
                        message: "Please select a state."
                      }
                    ]}
                  >
                    <Select
                      showSearch
                      placeholder="Search and select a state"
                      options={US_STATES}
                      optionFilterProp="label"
                      filterOption={(input, option) =>
                        String(option?.label || "")
                          .toLowerCase()
                          .includes(input.toLowerCase()) ||
                        String(option?.value || "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>

                  <Form.Item
                    label="City"
                    name="dealer_city"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a city."
                      }
                    ]}
                  >
                    <Input placeholder="City" />
                  </Form.Item>

                  <Form.Item
                    label="ZIP Code"
                    name="dealer_zip"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a ZIP code."
                      },
                      {
                        pattern: ZIP_PATTERN,
                        message: "Enter a valid 5-digit ZIP code."
                      }
                    ]}
                  >
                    <Input
                      placeholder="5-digit ZIP"
                      inputMode="numeric"
                      maxLength={5}
                      onChange={handleLocationZipChange}
                    />
                  </Form.Item>
                </Form>
              </div>
            ) : null}

            {currentStep === 1 ? (
              <div className="signup-step signup-step-search">
                <div className="signup-panel-header">
                  <h2>Step 2: Dealership Search</h2>
                  <p>
                    Search by dealership name. Select every matching dealership that belongs to your business.
                  </p>
                </div>

                <div className="signup-location-summary">
                  <span>{locationFilters?.dealer_state}</span>
                  <span>{locationFilters?.dealer_city}</span>
                  <span>{locationFilters?.dealer_zip}</span>
                </div>

                <Input.Search
                  allowClear
                  placeholder="Start typing a dealership name..."
                  value={searchText}
                  onChange={(event) => {
                    setSearchText(event.target.value);
                  }}
                  loading={isSearching}
                  className="signup-search"
                />

                <div className="dealership-selection-summary">
                  <div className="dealership-selected-title">
                    Selected Dealerships ({selectedDealerships.length})
                  </div>
                  <div className="dealership-selected-subtitle">
                    Select every listing that appears to be a duplicate of your dealership.
                  </div>
                </div>

                <div className="dealership-results-wrapper">
                  {isSearching ? (
                    <div className="dealership-results-spinner">
                      <Spin />
                    </div>
                  ) : (
                    <List
                      className="dealership-results"
                      bordered
                      dataSource={searchResults}
                      locale={{
                        emptyText:
                          searchText.trim().length < 2
                            ? "Type at least 2 characters to search."
                            : "No dealerships found."
                      }}
                      renderItem={(item) => {
                        const itemKey = getDealershipKey(item);
                        const isSelected = selectedDealershipKeys.includes(itemKey);
                        return (
                          <List.Item
                            className={`dealership-result-item${isSelected ? " selected" : ""}`}
                            onClick={() => toggleDealershipSelection(item)}
                          >
                            <div className="dealership-result-content">
                              <div className="dealership-result-header">
                                <div className="dealership-result-name">
                                  {item?.seller_name || "Unknown Dealership"}
                                </div>
                                <div className={`dealership-result-badge${isSelected ? " selected" : ""}`}>
                                  {isSelected ? "Selected" : "Select"}
                                </div>
                              </div>
                              {item?.dealership_group_name ? (
                                <div className="dealership-result-group">
                                  {item.dealership_group_name}
                                </div>
                              ) : null}
                              <div className="dealership-result-address">
                                {formatDealershipAddress(item)}
                              </div>
                              <div className="dealership-result-website">
                                {getDealershipWebsite(item) || "Website not listed"}
                              </div>
                              <div className="dealership-result-contact">
                                <span>{getDealershipEmail(item) || "Email not listed"}</span>
                                <span className="dealership-result-sep">•</span>
                                <span>{formatDealershipPhone(item) || "Phone not listed"}</span>
                              </div>
                            </div>
                          </List.Item>
                        );
                      }}
                    />
                  )}
                </div>

                {selectedDealerships.length > 0 ? (
                  <div className="dealership-selected">
                    <div className="dealership-selected-title">Selected Dealership Details</div>
                    <div className="dealership-selected-subtitle">
                      Review the locations that will be attached to this signup.
                    </div>
                    <div className="dealership-selected-list">
                      {selectedDealerships.map((dealership) => (
                        <div className="dealership-selected-card" key={getDealershipKey(dealership)}>
                          <Descriptions
                            bordered
                            column={1}
                            size="small"
                            className="dealership-selected-details"
                          >
                            <Descriptions.Item label="Dealership Name">
                              {dealership.seller_name || "Not listed"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Group Name">
                              {dealership.dealership_group_name || "Not listed"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Address">
                              {formatDealershipAddress(dealership) || "Not listed"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Website">
                              {getDealershipWebsite(dealership) || "Not listed"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Email">
                              {getDealershipEmail(dealership) || "Not listed"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Phone">
                              {formatDealershipPhone(dealership) || "Not listed"}
                            </Descriptions.Item>
                          </Descriptions>
                          <Button
                            className="dealership-selected-remove"
                            onClick={() => toggleDealershipSelection(dealership)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                    <div className="dealership-selected-hint">
                      Search again above to add or remove dealerships before proceeding.
                    </div>
                  </div>
                ) : null}
              </div>
            ) : null}

            {currentStep === 2 ? (
              <div className="signup-step signup-step-contact">
                <h3>Step 3: Primary Contact</h3>
                <Form
                  form={contactForm}
                  layout="vertical"
                  autoComplete="off"
                  className="signup-step2-form"
                  onValuesChange={updateStep2Ready}
                >
                  <Form.Item
                    label="Name"
                    name="contact_name"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Name!"
                      }
                    ]}
                  >
                    <Input placeholder="Full name" />
                  </Form.Item>
                  <Form.Item
                    label="Position"
                    name="contact_position"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Position!"
                      }
                    ]}
                  >
                    <Input placeholder="Job title" />
                  </Form.Item>
                  <Form.Item
                    label="Mobile Number"
                    name="contact_mobile"
                    rules={[
                      {
                        required: true,
                        message: "Please enter a valid Phone Number!",
                        pattern: PHONE_PATTERN
                      }
                    ]}
                  >
                    <Input
                      placeholder="Mobile number"
                      inputMode="numeric"
                      maxLength={10}
                      onChange={handleMobileChange}
                    />
                  </Form.Item>
                  <Form.Item
                    label="Email"
                    name="contact_email"
                    rules={[
                      {
                        required: true,
                        message: "Please enter valid Email!",
                        pattern: EMAIL_PATTERN
                      }
                    ]}
                  >
                    <Input placeholder="Email address" />
                  </Form.Item>
                </Form>
              </div>
            ) : null}

            {currentStep === 3 ? (
              <div className="signup-step signup-step-authorize">
                <h3>Step 4: Request for Authorization*</h3>
                <div className="signup-step3-body">
                  <div className="signup-step3-highlight">
                    NO MONTHLY SUBSCRIPTION COST. (free)
                  </div>
                  <p>
                    FastPass Referrals ("FPR") representing area credit unions; program
                    refers credit union members who have recently been "approved"
                    for an auto loan to authorized dealerships. Fill out the form to
                    be entered on the credit unions FastPass mobile app.
                  </p>
                  <div className="signup-step3-rules-title">
                    Buyer Referrals Exchange Rules
                  </div>
                  <p>Dealers agree to.</p>
                  <ol className="signup-step3-rules">
                    <li>
                      Accept funding instructions from originating credit union.
                    </li>
                    <li>
                      Unless instructed by referring credit union, dealership will
                      not process indirect
                    </li>
                    <li>
                      Email executed purchase agreement and stipulations to CU LIFT
                    </li>
                  </ol>
                  <p>
                    Program will promote authorized dealership inventory to credit
                    union auto shoppers
                  </p>
                  <div className="signup-step3-ack">
                    I have read the Program Rules.
                  </div>
                  <Radio.Group
                    onChange={(event) => setRulesAcknowledgement(event.target.value)}
                    value={rulesAcknowledgement}
                    className="signup-step3-radio"
                    buttonStyle="solid"
                  >
                    <Radio.Button value="yes">YES</Radio.Button>
                    <Radio.Button value="no">NO</Radio.Button>
                  </Radio.Group>
                </div>
              </div>
            ) : null}

            <div className="signup-step-actions">
              {currentStep > 0 ? (
                <Button
                  onClick={() => {
                    if (currentStep === 1) {
                      setSearchResults([]);
                      resetSelectedDealerships();
                    }
                    setCurrentStep(currentStep - 1);
                  }}
                >
                  Back
                </Button>
              ) : null}

              {currentStep === 0 ? (
                <Button type="primary" onClick={handleLocationNext}>
                  Next
                </Button>
              ) : null}

              {currentStep === 1 ? (
                <Button
                  type="primary"
                  disabled={selectedDealerships.length === 0}
                  onClick={() => setCurrentStep(2)}
                >
                  Next
                </Button>
              ) : null}

              {currentStep === 2 ? (
                <Button
                  type="primary"
                  disabled={!canProceedStep2}
                  onClick={() => setCurrentStep(3)}
                >
                  Next
                </Button>
              ) : null}

              {currentStep === 3 ? (
                <Button
                  type="primary"
                  disabled={rulesAcknowledgement !== "yes"}
                  onClick={handleFinish}
                >
                  Finish
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default DealershipSignup;
